/**
 * Click & Collect location picker — Figma 4501:12553 / WIL-206.
 * Reimplements the legacy APG contract with vanilla JavaScript.
 */
(function () {
  const STORAGE_KEY = 'mapCollectLocation';
  const COOKIE_KEY = 'selected_location';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
  const REQUEST_TIMEOUT = 10000;
  const DEFAULT_APG_ENDPOINT = 'https://iq.autopartsgroup.com.au/shopify/section/closestLocation';
  // APG's Cloudflare origin allowlist only covers the production storefront,
  // so local theme dev must route through scripts/click-collect-proxy.mjs.
  const LOCAL_PROXY_ENDPOINT = 'http://127.0.0.1:9293/closestLocation';

  /**
   * WIL-379 — header finder on pages with no `apgpartno`.
   * APG closestLocation only returns warehouses that stock a part; an empty
   * part number returns the "1" sentinel. These five published warehouses
   * (same addresses as the Click & Collect FAQ) are sorted by distance.
   * Live APG stock copy is only shown when a product part number is present.
   * Not merchant CMS (WIL-461): theme-editor location blocks would drift from
   * the five published APG warehouses the header finder needs.
   */
  const DIRECTORY_STORES = [
    {
      id: '4NSN',
      name: 'Auto Parts Group Smithfield, NSW',
      addressLine: '2/400 Woodpark Road',
      suburb: 'Smithfield',
      postcode: '2164',
      state: 'NSW',
      lat: '-33.83709617323125',
      long: '150.93454371243527',
      hours: 'Pick Up Hours: 8am – 4pm',
      rowPointer: '12429607-F597-409C-802D-572B92CEFBFA',
    },
    {
      id: '2VMB',
      name: 'Auto Parts Group VIC',
      addressLine: '25 Ibis Circuit',
      suburb: 'Dandenong South',
      postcode: '3175',
      state: 'VIC',
      lat: '-38.0372',
      long: '145.2498',
      hours: 'Pick Up Hours: 8am – 4pm Please Ring Buzzer',
      rowPointer: '6CAC9031-75FA-44A7-8ADD-F409017CF967',
    },
    {
      id: '1QBN',
      name: 'Auto Parts Group Brendale, QLD',
      addressLine: '73 Robertson Street',
      suburb: 'Brendale',
      postcode: '4500',
      state: 'QLD',
      lat: '-27.31290516923677',
      long: '152.96620031191347',
      hours: 'Pick Up Hours: 8am – 4pm',
      rowPointer: '7D3552CD-C99A-465C-9A09-153181D82AAB',
    },
    {
      id: '5SAL',
      name: 'Auto Parts Group Kilburn, SA',
      addressLine: '60 Grand Junction Road',
      suburb: 'Kilburn',
      postcode: '5084',
      state: 'SA',
      lat: '-34.849207297570295',
      long: '138.58555803813994',
      hours: 'Pick Up Hours: 8am – 4pm',
      rowPointer: '3B39A8AB-3962-4612-A704-E3733E43E4AF',
    },
    {
      id: '3WPT',
      name: 'Auto Parts Group Welshpool, WA',
      addressLine: '3 Fargo Way',
      suburb: 'Welshpool',
      postcode: '6106',
      state: 'WA',
      lat: '-31.99634179553609',
      long: '115.97383493798432',
      hours: 'Pick Up Hours: 8am – 4pm Please Ring Buzzer',
      rowPointer: '7A9E8DF0-37B6-4251-9803-4359F569B2F1',
    },
  ];

  /** @param {string} [configured] */
  function resolveApgEndpoint(configured) {
    if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
      return LOCAL_PROXY_ENDPOINT;
    }
    return configured || DEFAULT_APG_ENDPOINT;
  }

  /**
   * @typedef {Object} CollectStore
   * @property {string} id Exact APG `location` value used by clickNcollect.
   * @property {string} name
   * @property {string} pdpLabel
   * @property {string} addressLine
   * @property {string} suburb
   * @property {string} postcode
   * @property {string} availability
   * @property {string} mapUrl
   * @property {string} state
   * @property {string} lat
   * @property {string} long
   * @property {string} rowPointer
   */

  /** @param {unknown} value */
  function asString(value) {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  }

  /** @param {Record<string, unknown>} value @returns {CollectStore | null} */
  function normaliseStore(value) {
    const name = asString(value.name);
    if (!name) return null;

    const addressLine = asString(value.addressLine || value.address || value.address_line_1);
    const postcode = asString(value.postcode);
    const lat = asString(value.lat);
    const long = asString(value.long || value.lng);
    const mapQuery = lat && long ? `${lat},${long}` : [addressLine, postcode, 'Australia'].filter(Boolean).join(' ');

    return {
      id: asString(value.id || value.location),
      name,
      pdpLabel: asString(value.pdpLabel) || name.replace(/^Auto Parts [Gg]roup\s*/, ''),
      addressLine,
      suburb: asString(value.suburb),
      postcode,
      availability: asString(value.availability || value.available || value.comments),
      mapUrl: asString(value.mapUrl) || (mapQuery ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}` : ''),
      state: asString(value.state),
      lat,
      long,
      rowPointer: asString(value.rowPointer || value.RowPointer),
    };
  }

  /** Single source in map-utils.js (WIL-461). @param {string} name */
  function readCookie(name) {
    return window.MapUtils.readCookie(name);
  }

  /** @returns {CollectStore | null} */
  function readStoredLocation() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = normaliseStore(JSON.parse(raw));
        if (stored) return stored;
      }
    } catch {
      /* Fall through to the legacy cookie. */
    }

    const cookie = readCookie(COOKIE_KEY);
    if (!cookie) return null;

    try {
      return normaliseStore(JSON.parse(cookie));
    } catch {
      return null;
    }
  }

  /** @param {CollectStore | null} store */
  async function writeCartCollectAttributes(store) {
    const root = window.Shopify?.routes?.root || '/';
    const attributes = store
      ? {
          clickNcollect: store.id,
          'Pickup Location': store.name,
          'Pickup Address': [store.addressLine, store.postcode].filter(Boolean).join(' '),
        }
      : {
          clickNcollect: '',
          'Pickup Location': '',
          'Pickup Address': '',
        };

    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
    } catch (error) {
      console.warn('Click & Collect cart attribute write failed.', error);
    }
  }

  /** @param {CollectStore | null} store */
  function writeStoredLocation(store) {
    if (!store) {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* Cookie and cart cleanup still proceed when storage is unavailable. */
      }
      document.cookie = `${COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax`;
      void writeCartCollectAttributes(null);
      window.dispatchEvent(new CustomEvent('map:collect-location-selected', { detail: null }));
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
      /* The cookie remains the persistence fallback. */
    }

    // Preserve every production cookie field and the seven-day TTL. `location`
    // is an additive compatibility field so cart attributes can be restored
    // after localStorage is cleared; legacy consumers ignore unknown fields.
    const cookieData = {
      location: store.id,
      name: store.name,
      address_line_1: store.addressLine,
      postcode: store.postcode,
      lat: store.lat,
      long: store.long,
      state: store.state,
      comments: store.availability,
      RowPointer: store.rowPointer,
    };
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(JSON.stringify(cookieData))}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;

    void writeCartCollectAttributes(store);
    window.dispatchEvent(new CustomEvent('map:collect-location-selected', { detail: store }));
  }

  /**
   * @param {string} url
   * @param {RequestInit} [options]
   */
  async function fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('timeout');
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  /**
   * @param {string} query
   * @param {string} apiKey
   * @returns {Promise<{ lat: number, lng: number }>}
   */
  async function geocodeLocation(query, apiKey) {
    const params = new URLSearchParams({ key: apiKey });
    if (/^\d{4}$/.test(query)) {
      params.set('components', `postal_code:${query}|country:AU`);
    } else {
      params.set('address', `${query}, Australia`);
      params.set('components', 'country:AU');
    }

    const response = await fetchWithTimeout(`https://maps.googleapis.com/maps/api/geocode/json?${params}`);
    if (!response.ok) throw new Error('geocode');

    const payload = await response.json();
    const location = payload?.status === 'OK' ? payload.results?.[0]?.geometry?.location : null;
    if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
      throw new Error('geocode');
    }

    return location;
  }

  /**
   * @param {number} latitude
   * @param {number} longitude
   * @param {string} partNumber
   * @param {string} endpoint
   */
  async function fetchClosestLocations(latitude, longitude, partNumber, endpoint) {
    const body = new URLSearchParams({
      latitude: String(latitude),
      longitude: String(longitude),
      apgpartno: partNumber,
    });
    const response = await fetchWithTimeout(resolveApgEndpoint(endpoint), {
      method: 'POST',
      headers: {
        Accept: 'text/html, */*; q=0.01',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body,
    });
    if (!response.ok) throw new Error('api');
    return response.text();
  }

  /**
   * Convert the APG HTML response to theme-owned data. The returned markup is
   * never inserted into the storefront DOM.
   * @param {string} html
   * @returns {CollectStore[]}
   */
  function parseStoresFromHtml(html) {
    if (!html.trim() || html.trim() === '1') return [];

    const documentResult = new DOMParser().parseFromString(html, 'text/html');
    const stores = [];
    const seenIds = new Set();

    documentResult.querySelectorAll('.location_list').forEach((card) => {
      const source = card.querySelector('button.search_location') || card.querySelector('[location][name]');
      if (!(source instanceof HTMLElement)) return;

      const id = source.getAttribute('location') || '';
      const name = source.getAttribute('name') || card.querySelector('b')?.textContent?.trim() || '';
      if (!id || !name || seenIds.has(id)) return;

      const store = normaliseStore({
        id,
        name,
        addressLine: source.getAttribute('address') || '',
        postcode: source.getAttribute('postcode') || '',
        availability: source.getAttribute('available') || card.querySelector('.available')?.textContent?.trim() || '',
        lat: source.getAttribute('lat') || '',
        long: source.getAttribute('long') || '',
        state: source.getAttribute('state') || '',
        rowPointer: source.getAttribute('RowPointer') || '',
      });
      if (!store) return;

      seenIds.add(id);
      stores.push(store);
    });

    return stores;
  }

  function currentProductPagePartNumber() {
    const trigger = document.querySelector('[data-product-logistics-collect][data-apg-part-number]');
    return trigger instanceof HTMLElement ? trigger.dataset.apgPartNumber || '' : '';
  }

  function currentApgPartNumber() {
    // Set by cart-drawer.js when the picker is opened for a bulky cart line —
    // that part number wins over whichever PDP is open behind the cart.
    const drawer = document.getElementById('ClickCollectLocationDrawer');
    if (drawer instanceof HTMLElement && drawer.dataset.apgOverride) {
      return drawer.dataset.apgOverride;
    }

    const productPart = currentProductPagePartNumber();
    if (productPart) return productPart;

    const cartForm = document.querySelector('[data-cart-items][data-cart-apg-part-number]');
    return cartForm instanceof HTMLElement ? cartForm.dataset.cartApgPartNumber || '' : '';
  }

  function apiConfiguration() {
    const panels = document.querySelectorAll('[data-click-collect-panel]');
    let googleApiKey = '';
    let endpoint = DEFAULT_APG_ENDPOINT;
    panels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      if (!googleApiKey && panel.dataset.googleApiKey) googleApiKey = panel.dataset.googleApiKey;
      if (panel.dataset.closestLocationEndpoint) endpoint = panel.dataset.closestLocationEndpoint;
    });
    return { googleApiKey, endpoint };
  }

  /** @param {HTMLElement} root */
  function panelPartNumber(root) {
    if (root.hasAttribute('data-header-location-finder')) return currentProductPagePartNumber();
    return currentApgPartNumber();
  }

  /**
   * @param {number} lat1
   * @param {number} lng1
   * @param {number} lat2
   * @param {number} lng2
   */
  function distanceKm(lat1, lng1, lat2, lng2) {
    const toRad = (value) => (value * Math.PI) / 180;
    const earthKm = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return earthKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /**
   * A suburb or postcode search used to return every warehouse, closest first,
   * so the query only changed the order. Keep stores that match the text, or
   * that sit in the same metro as the geocoded place. These five warehouses
   * are hundreds of kilometres apart; 150km covers each metro (Geelong,
   * Wollongong, Gold Coast) without pulling in the next state.
   */
  const NEARBY_STORE_RADIUS_KM = 150;

  const STATE_SEARCH_NAMES = {
    ACT: 'australian capital territory',
    NSW: 'new south wales',
    NT: 'northern territory',
    QLD: 'queensland',
    SA: 'south australia',
    TAS: 'tasmania',
    VIC: 'victoria',
    WA: 'western australia',
  };

  /** @param {string} value */
  function normaliseSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** @returns {CollectStore[]} */
  function normalisedDirectory() {
    return DIRECTORY_STORES.map((entry) => normaliseStore(entry)).filter((store) => store !== null);
  }

  /** @param {CollectStore} store */
  function storeSearchHaystack(store) {
    const stateName = STATE_SEARCH_NAMES[store.state] || '';
    return normaliseSearchText(
      [store.name, store.addressLine, store.suburb, store.postcode, store.state, stateName].filter(Boolean).join(' '),
    );
  }

  /**
   * @param {CollectStore[]} stores
   * @param {string} query
   * @returns {CollectStore[]}
   */
  function filterStoresByQuery(stores, query) {
    const tokens = normaliseSearchText(query).split(' ').filter(Boolean);
    if (!tokens.length) return stores.slice();
    return stores.filter((store) => {
      const haystack = storeSearchHaystack(store);
      return tokens.every((token) => haystack.includes(token));
    });
  }

  /**
   * @param {CollectStore[]} stores
   * @param {number} latitude
   * @param {number} longitude
   * @param {number} [maxKm] Omit to keep every store, nearest first (geolocate).
   * @returns {CollectStore[]}
   */
  function rankStoresByDistance(stores, latitude, longitude, maxKm) {
    const ranked = stores
      .map((store) => {
        const lat = Number(store.lat);
        const lng = Number(store.long);
        return {
          store,
          distance:
            Number.isFinite(lat) && Number.isFinite(lng)
              ? distanceKm(latitude, longitude, lat, lng)
              : Number.POSITIVE_INFINITY,
        };
      })
      .sort((a, b) => a.distance - b.distance);

    if (typeof maxKm !== 'number') return ranked.map((row) => row.store);

    return ranked
      .filter((row) => Number.isFinite(row.distance) && row.distance <= maxKm)
      .map((row) => row.store);
  }

  /**
   * @param {number} latitude
   * @param {number} longitude
   * @returns {CollectStore[]}
   */
  function sortDirectoryStores(latitude, longitude) {
    return rankStoresByDistance(normalisedDirectory(), latitude, longitude);
  }

  /**
   * APG only returns stores that stock the given part. Callers without a part
   * (header finder on home/PLP/content) use the published warehouse directory
   * instead of sending an empty `apgpartno` (WIL-379).
   * @param {number} latitude
   * @param {number} longitude
   * @param {string} [partNumber]
   * @returns {Promise<CollectStore[]>}
   */
  async function searchLocationsByCoordinates(latitude, longitude, partNumber) {
    const { endpoint } = apiConfiguration();
    const part = partNumber === undefined ? currentApgPartNumber() : String(partNumber || '');
    if (!part) return sortDirectoryStores(latitude, longitude);
    const html = await fetchClosestLocations(latitude, longitude, part, endpoint);
    return parseStoresFromHtml(html);
  }

  /**
   * @param {string} query
   * @param {string} [partNumber]
   * @returns {Promise<CollectStore[]>}
   */
  async function searchLocationsByQuery(query, partNumber) {
    const part = partNumber === undefined ? currentApgPartNumber() : String(partNumber || '');
    const { googleApiKey } = apiConfiguration();

    if (!part) {
      const directory = normalisedDirectory();
      const textMatches = filterStoresByQuery(directory, query);
      if (textMatches.length) return textMatches;
      if (!googleApiKey) return [];
      try {
        const coordinates = await geocodeLocation(query, googleApiKey);
        return rankStoresByDistance(directory, coordinates.lat, coordinates.lng, NEARBY_STORE_RADIUS_KM);
      } catch (error) {
        if (error instanceof Error && error.message === 'geocode') return [];
        throw error;
      }
    }

    if (!googleApiKey) throw new Error('config');
    const coordinates = await geocodeLocation(query, googleApiKey);
    const stores = await searchLocationsByCoordinates(coordinates.lat, coordinates.lng, part);
    const textMatches = filterStoresByQuery(stores, query);
    if (textMatches.length) return textMatches;
    return rankStoresByDistance(stores, coordinates.lat, coordinates.lng, NEARBY_STORE_RADIUS_KM);
  }

  /** @param {CollectStore} store */
  function storeBrand(store) {
    return /repco/i.test(store.name) ? 'repco' : 'map';
  }

  /**
   * @param {CollectStore} store
   * @param {boolean} checked
   * @param {string} groupName
   * @param {string} [availabilityFallback]
   * @param {{ map?: string, repco?: string }} [logos]
   */
  function renderStoreOption(store, checked, groupName, availabilityFallback = '', logos = {}) {
    const label = document.createElement('label');
    label.className = 'click-collect-location-panel__store-option';
    label.dataset.storeId = store.id;
    label.innerHTML = `
      <span class="click-collect-location-panel__store-main">
        <span class="click-collect-location-panel__store-control">
          <input type="radio" class="click-collect-location-panel__store-input">
        </span>
        <span class="click-collect-location-panel__store-copy">
          <span class="click-collect-location-panel__store-name"></span>
          <span class="click-collect-location-panel__store-address"></span>
          <span class="click-collect-location-panel__store-availability"></span>
        </span>
      </span>
      <span class="click-collect-location-panel__store-logo"></span>
    `;

    const input = label.querySelector('input');
    if (input instanceof HTMLInputElement) {
      input.name = groupName;
      input.value = store.id;
      input.checked = checked;
    }

    const name = label.querySelector('.click-collect-location-panel__store-name');
    const address = label.querySelector('.click-collect-location-panel__store-address');
    const availability = label.querySelector('.click-collect-location-panel__store-availability');
    const logo = label.querySelector('.click-collect-location-panel__store-logo');
    const availabilityText = store.availability || availabilityFallback;
    const brand = storeBrand(store);
    const logoSrc = brand === 'repco' ? logos.repco : logos.map;
    if (name) name.textContent = store.name;
    if (address) address.textContent = [store.addressLine, store.postcode].filter(Boolean).join(' ');
    if (availability) {
      availability.textContent = availabilityText;
      availability.hidden = !availabilityText;
    }
    if (logo instanceof HTMLElement && logoSrc) {
      const img = document.createElement('img');
      img.className = 'click-collect-location-panel__store-logo-img';
      img.src = logoSrc;
      img.alt = '';
      img.dataset.brand = brand;
      if (brand === 'repco') {
        img.width = 80;
        img.height = 15;
      } else {
        img.width = 42;
        img.height = 16;
      }
      logo.appendChild(img);
    }
    return label;
  }

  /** @param {HTMLElement} root */
  function initClickCollectPanel(root) {
    if (root.dataset.clickCollectInit === 'true') return;
    root.dataset.clickCollectInit = 'true';

    const groupName = `click-collect-store-${root.closest('dialog')?.id || 'default'}`;
    const picker = root.querySelector('[data-click-collect-picker]');
    const selectedView = root.querySelector('[data-click-collect-selected]');
    const storeList = root.querySelector('[data-click-collect-store-list]');
    const selectBtn = root.querySelector('[data-click-collect-select]');
    const changeBtn = root.querySelector('[data-click-collect-change]');
    const searchInput = root.querySelector('[data-click-collect-search]');
    const searchBtn = root.querySelector('[data-click-collect-search-btn]');
    const geolocateBtn = root.querySelector('[data-click-collect-geolocate]');
    const status = root.querySelector('[data-click-collect-status]');
    const clearPill = root.querySelector('[data-click-collect-clear-pill]');
    const networkNote = root.querySelector('[data-click-collect-network-note]');
    const defaultEta = root.dataset.clickCollectDefaultEta || 'Parts available to collect in 2-3 days';
    const isHeaderFinder = root.hasAttribute('data-header-location-finder');
    const logos = {
      map: root.dataset.logoMap || '',
      repco: root.dataset.logoRepco || '',
    };

    /** @type {CollectStore[]} */
    let stores = [];
    /** @type {CollectStore | null} */
    let pendingSelection = null;
    /** @type {CollectStore | null} */
    let confirmedSelection = readStoredLocation();
    let loading = false;
    let lastQuery = '';

    /** @param {string} message */
    function showStatus(message) {
      if (!(status instanceof HTMLElement)) return;
      status.textContent = message;
      status.hidden = false;
    }

    function hideStatus() {
      if (status instanceof HTMLElement) status.hidden = true;
    }

    function syncSearchButton() {
      if (!(searchBtn instanceof HTMLButtonElement)) return;
      const query = searchInput instanceof HTMLInputElement ? searchInput.value.trim() : '';
      searchBtn.disabled = loading || !query;
      searchBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    /** @param {boolean} value */
    function setLoading(value) {
      loading = value;
      if (geolocateBtn instanceof HTMLButtonElement) geolocateBtn.disabled = value;
      syncSearchButton();
    }

    /** @param {CollectStore[]} list @param {string} [emptyMessage] */
    function renderStoreList(list, emptyMessage = '') {
      if (!(storeList instanceof HTMLElement)) return;
      storeList.replaceChildren();
      if (networkNote instanceof HTMLElement) networkNote.hidden = !list.length;

      if (!list.length) {
        if (emptyMessage) showStatus(emptyMessage);
        else hideStatus();
        if (selectBtn instanceof HTMLButtonElement) selectBtn.disabled = true;
        return;
      }

      hideStatus();
      list.forEach((store) => {
        storeList.appendChild(
          renderStoreOption(store, pendingSelection?.id === store.id, groupName, defaultEta, logos),
        );
      });
      if (selectBtn instanceof HTMLButtonElement) selectBtn.disabled = !pendingSelection;
    }

    /** @param {CollectStore | null} store */
    function populateSelectedView(store) {
      const nameTargets = root.querySelectorAll('[data-click-collect-selected-name], [data-click-collect-selected-name-link]');
      const addressTarget = root.querySelector('[data-click-collect-selected-address]');
      const mapLink = root.querySelector('[data-click-collect-selected-map]');
      const etaTarget = root.querySelector('[data-click-collect-selected-eta]');

      nameTargets.forEach((element) => {
        element.textContent = store?.name || '';
      });
      if (addressTarget instanceof HTMLElement) {
        addressTarget.textContent = store ? [store.addressLine, store.postcode].filter(Boolean).join(' ') : '';
      }
      if (mapLink instanceof HTMLAnchorElement) {
        mapLink.href = store?.mapUrl || '#';
        mapLink.hidden = !store?.mapUrl;
      }
      if (etaTarget instanceof HTMLElement) {
        etaTarget.textContent = store?.availability || defaultEta;
        etaTarget.hidden = !etaTarget.textContent;
      }
    }

    function showPickerView() {
      if (picker instanceof HTMLElement) picker.hidden = false;
      if (selectedView instanceof HTMLElement) selectedView.hidden = true;
      if (selectBtn instanceof HTMLButtonElement) selectBtn.hidden = false;
      if (changeBtn instanceof HTMLButtonElement) changeBtn.hidden = true;
      renderStoreList(stores);
    }

    function showSelectedView() {
      if (picker instanceof HTMLElement) picker.hidden = true;
      if (selectedView instanceof HTMLElement) selectedView.hidden = false;
      if (selectBtn instanceof HTMLButtonElement) selectBtn.hidden = true;
      if (changeBtn instanceof HTMLButtonElement) changeBtn.hidden = false;
      if (networkNote instanceof HTMLElement) networkNote.hidden = true;
      populateSelectedView(confirmedSelection);
    }

    function syncPanelView() {
      confirmedSelection = readStoredLocation();
      pendingSelection = confirmedSelection?.id ? confirmedSelection : null;
      if (confirmedSelection) showSelectedView();
      else showPickerView();
    }

    /** @param {unknown} error */
    function searchErrorMessage(error) {
      if (error instanceof Error && error.message === 'config') {
        return root.dataset.clickCollectConfigError || 'Store search is not configured.';
      }
      if (error instanceof Error && error.message === 'timeout') {
        return root.dataset.clickCollectTimeoutError || 'Store search timed out. Please try again.';
      }
      if (error instanceof Error && error.message === 'geocode') {
        return root.dataset.clickCollectGeocodeError || "We couldn't find that Australian suburb or postcode.";
      }
      return root.dataset.clickCollectApiError || 'Pickup stores are temporarily unavailable.';
    }

    async function searchLocations() {
      const query = searchInput instanceof HTMLInputElement ? searchInput.value.trim() : '';
      if (!query || loading) return;

      setLoading(true);
      showStatus(root.dataset.clickCollectSearchingMessage || 'Searching nearby pickup stores…');
      stores = [];
      pendingSelection = null;
      renderStoreList([]);

      try {
        stores = await searchLocationsByQuery(query, panelPartNumber(root));
        lastQuery = query;
        pendingSelection = stores[0] || null;
        renderStoreList(stores, root.dataset.clickCollectNoLocations || 'No pickup stores were found nearby.');
      } catch (error) {
        stores = [];
        renderStoreList([]);
        showStatus(searchErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }

    async function searchCurrentLocation() {
      if (loading) return;
      if (!navigator.geolocation) {
        showStatus(root.dataset.clickCollectUnsupported || 'Your browser does not support location access.');
        return;
      }

      setLoading(true);
      showStatus(root.dataset.clickCollectSearchingMessage || 'Searching nearby pickup stores…');
      stores = [];
      pendingSelection = null;
      renderStoreList([]);

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: REQUEST_TIMEOUT,
            maximumAge: 300000,
          });
        });
        stores = await searchLocationsByCoordinates(
          position.coords.latitude,
          position.coords.longitude,
          panelPartNumber(root),
        );
        pendingSelection = stores[0] || null;
        renderStoreList(stores, root.dataset.clickCollectNoLocations || 'No pickup stores were found nearby.');
      } catch (error) {
        const geolocationCode =
          typeof error === 'object' && error !== null && 'code' in error ? Number(error.code) : 0;
        stores = [];
        renderStoreList([]);
        if (geolocationCode === 1) {
          showStatus(root.dataset.clickCollectPermissionDenied || 'Location permission was not granted.');
        } else {
          showStatus(searchErrorMessage(error));
        }
      } finally {
        setLoading(false);
      }
    }

    renderStoreList([]);
    syncPanelView();

    if (searchInput instanceof HTMLInputElement) {
      syncSearchButton();
      searchInput.addEventListener('input', () => {
        syncSearchButton();
        if (searchInput.value.trim() !== lastQuery) {
          stores = [];
          pendingSelection = null;
          renderStoreList([]);
          hideStatus();
          if (selectBtn instanceof HTMLButtonElement) selectBtn.disabled = true;
        }
      });
      searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || !(searchBtn instanceof HTMLButtonElement) || searchBtn.disabled) return;
        event.preventDefault();
        searchBtn.click();
      });
    }

    searchBtn?.addEventListener('click', () => {
      void searchLocations();
    });

    geolocateBtn?.addEventListener('click', () => {
      void searchCurrentLocation();
    });

    storeList?.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
      pendingSelection = stores.find((store) => store.id === target.value) || null;
      if (selectBtn instanceof HTMLButtonElement) selectBtn.disabled = !pendingSelection;
    });

    selectBtn?.addEventListener('click', () => {
      if (!pendingSelection) return;
      confirmedSelection = pendingSelection;
      writeStoredLocation(confirmedSelection);
      showSelectedView();
      if (!isHeaderFinder) closeDrawer(root);
    });

    changeBtn?.addEventListener('click', () => {
      pendingSelection = confirmedSelection?.id ? confirmedSelection : null;
      showPickerView();
    });

    clearPill?.addEventListener('click', () => {
      pendingSelection = null;
      confirmedSelection = null;
      writeStoredLocation(null);
      showPickerView();
    });

    root.closest('dialog')?.addEventListener('close', syncPanelView);
    window.addEventListener('map:collect-location-selected', syncPanelView);
  }

  /** @param {HTMLElement} root */
  function closeDrawer(root) {
    const drawer = root.closest('dialog');
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;
    if (window.MapUI?.closeDrawer) window.MapUI.closeDrawer(drawer);
    else drawer.close();
  }

  /** @param {HTMLElement} row */
  function initProductLogisticsRow(row) {
    const title = row.querySelector('[data-product-logistics-title-text]');
    const detail = row.querySelector('[data-product-logistics-detail]');
    const stock = row.querySelector('[data-product-logistics-stock]');
    if (!(title instanceof HTMLElement) || !(detail instanceof HTMLElement)) return;

    const defaultTitle = row.dataset.logisticsDefaultTitle || title.textContent || '';
    const defaultDetail = row.dataset.logisticsDefaultDetail || detail.textContent || '';
    const selectedDetail = row.dataset.logisticsSelectedDetail || defaultDetail;

    function render() {
      const store = readStoredLocation();
      if (!store) {
        title.textContent = defaultTitle;
        detail.textContent = defaultDetail;
        if (stock instanceof HTMLElement) stock.hidden = true;
        return;
      }

      title.textContent = store.pdpLabel || store.name;
      detail.textContent = selectedDetail;
      if (stock instanceof HTMLElement) {
        stock.textContent = store.availability;
        stock.hidden = !store.availability;
      }
    }

    render();
    window.addEventListener('map:collect-location-selected', render);
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) render();
    });
  }

  function initClickCollect() {
    document.querySelectorAll('[data-click-collect-panel]').forEach((panel) => {
      if (panel instanceof HTMLElement) initClickCollectPanel(panel);
    });
    document.querySelectorAll('[data-product-logistics-collect]').forEach((row) => {
      if (row instanceof HTMLElement) initProductLogisticsRow(row);
    });
  }

  // Opening the picker from a PDP row means searching for *this* product —
  // drop any part-number override a cart flow left on the drawer.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-product-logistics-collect]')) return;
    const drawer = document.getElementById('ClickCollectLocationDrawer');
    if (drawer instanceof HTMLElement) delete drawer.dataset.apgOverride;
  });

  /** @type {Window & { MapClickCollect?: Record<string, unknown> }} */
  const win = window;
  win.MapClickCollect = {
    readStoredLocation,
    writeStoredLocation,
    initClickCollectPanel,
    parseStoresFromHtml,
    searchLocationsByCoordinates,
    searchLocationsByQuery,
    currentApgPartNumber,
    currentProductPagePartNumber,
    directoryStores: DIRECTORY_STORES,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initClickCollect);
  } else {
    initClickCollect();
  }
  document.addEventListener('shopify:section:load', initClickCollect);
})();
